import { Tag, Tooltip, useTestIds } from "@homebound/beam";
import { pluralize } from "src/utils";
import { formatTooltipOption } from "./OptionsTooltipContent";
import { RequiredWhenTag_RequiredWhenFragment } from "./RequiredWhenTag.generated";

type RequiredWhenTagProps = { requiredWhen: RequiredWhenTag_RequiredWhenFragment[] };

/** Shows a `N Required Whens` tag for a group, with the clauses themselves in a tooltip. */
export function RequiredWhenTag(props: RequiredWhenTagProps) {
  const { requiredWhen } = props;
  const tid = useTestIds({});
  const count = requiredWhen.length;
  return (
    <Tooltip title={<RequiredWhenTooltipContent requiredWhen={requiredWhen} />} placement="top">
      <Tag {...tid.requiredWhenTag} text={`${count} ${pluralize(count, "Required When")}`} type="update" />
    </Tooltip>
  );
}

/** Lists one clause per line, i.e. `Option A AND Option B AND NOT (Option C AND Option D)`. */
export function RequiredWhenTooltipContent(props: RequiredWhenTagProps) {
  const { requiredWhen } = props;
  const tid = useTestIds(props, "requiredWhenTooltip");
  return (
    <div {...tid}>
      <div>{`${pluralize(requiredWhen, "Required When")}:`}</div>
      {/* The group is required when *any* clause matches, and an empty list means it never is */}
      {requiredWhen.isEmpty ? (
        <div {...tid.clause}>Never required</div>
      ) : (
        requiredWhen.map((clause) => (
          // Clauses have no id of their own, so key off the options they mention
          <div
            key={clause.all
              .concat(clause.none)
              .map((o) => o.id)
              .join()}
            {...tid.clause}
          >
            {formatClause(clause)}
          </div>
        ))
      )}
    </div>
  );
}

/** I.e. `ADDMEDIA01 Add Media Room (Media Room 299) AND NOT (OPT2 Sink (Level 1 > Kitchen))`. */
function formatClause(clause: RequiredWhenTag_RequiredWhenFragment) {
  const { all, none } = clause;
  const terms = all.map((o) => formatTooltipOption(o));
  if (none.nonEmpty) terms.push(`NOT (${none.map((o) => formatTooltipOption(o)).join(" AND ")})`);
  // A clause with no options at all matches everything, i.e. the group is always required
  return terms.isEmpty ? "Always" : terms.join(" AND ");
}
